/**
 * Wallet service สำหรับ user-htd — ใช้ API เดียวกับ user-classic / user-modern
 * GET /api/v1/wallet/tenant/:tenantId/users/:userId/wallet
 */
import * as authService from './authService'
import { handleUnauthorizedResponse } from '../utils/sessionReplacedHandler'
import { getRuntimeTenantIdSync } from '../lib/runtimeTenant'
import { withApiBaseUrl } from '../lib/apiBaseUrl'

export interface WalletBalance {
  main: number
  bonus: number
  pending: number
  frozen: number
}

export interface WalletData {
  userId: string
  tenantId: string
  balance: WalletBalance
  currency: string
  status: string
}

export interface WalletResponse {
  success: boolean
  data?: WalletData
  error?: string
}

export async function getWallet(): Promise<WalletResponse> {
  const user = authService.getCurrentUser()
  const token = authService.getAccessToken()
  const tenantId = user?.tenantId || getRuntimeTenantIdSync()
  const userId = user?.id || (user as { _id?: string } | null)?._id
  if (!userId || !tenantId || !token) {
    return { success: false, error: 'ไม่พบข้อมูลผู้ใช้' }
  }
  try {
    const res = await fetch(
      withApiBaseUrl(`/api/v1/wallet/tenant/${tenantId}/users/${userId}/wallet`),
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-Tenant-ID': tenantId,
        },
        credentials: 'include',
      }
    )
    const data = (await res.json()) as WalletResponse & { code?: string; message?: string }
    if (await handleUnauthorizedResponse(res.status, data)) {
      return { success: false, error: data?.code === 'SESSION_REPLACED' ? 'SESSION_REPLACED' : 'AUTH_EXPIRED' }
    }
    if (!res.ok) return { success: false, error: data.error || 'ดึงข้อมูล wallet ไม่สำเร็จ' }
    return data
  } catch (e) {
    return { success: false, error: (e instanceof Error ? e.message : 'เกิดข้อผิดพลาด') }
  }
}

/** ยอดเครดิตที่ใช้แสดง = หลัก + โบนัส (ตรงกับ user-classic) */
export function getDisplayBalance(wallet: WalletData | null | undefined): number {
  if (!wallet?.balance) return 0
  const b = wallet.balance
  return (b.main ?? 0) + (b.bonus ?? 0)
}

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('th-TH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount)
}
